import { FC, ReactNode, useState } from 'react'

import { InfiniteData, useQueryClient } from '@tanstack/react-query'

import {
  Button,
  Cluster,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@forto-dev/phoenix-ui-core'

import { CustomerRequiredAction, RequiredActionTask, RequiredActionV2 } from '@/features/workbench'
import { COMPANY_DOCUMENT_UPLOAD_ACTION_TYPES, TaskType } from '@/features/workbench'
import { useLocalisation } from '@/utils/localisation'

import { ActionAddContainerNumbers } from './actionAddContainerNumbersNew'
import { ActionAddSealNumbers } from './actionAddSealNumbersNew'
import { ActionFileUpload } from './actionFileUploadNew'
import { ActionHBLTypeSelector } from './actionHBLSelectorNew'
import { ActionIncotermSelector } from './actionIncotermSelectorNew'
import { AuthorizeBookingDialog } from './authorizeBookingDialogNew'

type RequiredActionsQueryData = InfiniteData<CustomerRequiredAction>

export type ActionDialogProps = {
  action: RequiredActionV2 | null
  setOpen: (open: RequiredActionV2 | null) => void
}

export const GenericActionDialog: FC<ActionDialogProps> = ({ action, setOpen }) => {
  const { translate } = useLocalisation()
  const [submitCallback, setSubmitCallback] = useState<() => Promise<void>>()
  const [submitting, setSubmitting] = useState(false)
  const queryClient = useQueryClient()

  const isCompanyDocumentUploadAction = (type: TaskType | undefined): type is TaskType =>
    !!type && COMPANY_DOCUMENT_UPLOAD_ACTION_TYPES.includes(type)

  const renderActionInput = (action: RequiredActionV2 | null): ReactNode => {
    if (!action) return null
    if (
      action.category === 'documentUpload' ||
      action.category === 'downloadTemplateSignUpload' ||
      isCompanyDocumentUploadAction(action.type) // Hotfix for missing category in required action
    ) {
      return <ActionFileUpload action={action} setSubmitCallback={(fn) => setSubmitCallback(() => fn)} />
    }
    if (action.type === 'provideIncoTerms') {
      return <ActionIncotermSelector action={action} setSubmitCallback={(fn) => setSubmitCallback(() => fn)} />
    }
    if (action.type === 'provideBLType') {
      return (
        <ActionHBLTypeSelector
          action={action}
          setSubmitCallback={(fn) => {
            setSubmitCallback(() => fn)
          }}
        />
      )
    }
    if (action.type === 'provideContainerNumbers') {
      return <ActionAddContainerNumbers action={action} setSubmitCallback={(fn) => setSubmitCallback(() => fn)} />
    }
    if (action.type === 'provideSealNumbers') {
      return <ActionAddSealNumbers action={action} setSubmitCallback={(fn) => setSubmitCallback(() => fn)} />
    }
    return null
  }

  const closeDialog = () => {
    setSubmitCallback(undefined)
    setOpen(null)
  }

  const onSaveHandler = async () => {
    if (!submitCallback || !action) return

    setSubmitting(true)

    const matchingQueries = queryClient.getQueriesData<RequiredActionsQueryData>({
      predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === 'required-actions',
    })
    matchingQueries.forEach(([queryKey, data]) => {
      if (!data) return

      const updated: RequiredActionsQueryData = {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          tasks: page.tasks.filter((task: RequiredActionTask) => task.taskDefinitionAttributes.id !== action.id),
        })),
      }
      queryClient.setQueryData(queryKey, updated)
    })

    await submitCallback()

    setSubmitting(false)
    closeDialog()
  }

  return (
    <Dialog open={!!action} onOpenChange={(open) => setOpen(open ? action : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{action && action.type && translate(`required-actions.action.${action.type}`)}</DialogTitle>
        </DialogHeader>
        {renderActionInput(action)}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline-primary" onClick={closeDialog}>
              {translate('required-actions.dialog.cancel')}
            </Button>
          </DialogClose>
          <Button variant="primary" onClick={onSaveHandler} disabled={!submitCallback || submitting}>
            <Cluster gap={8} y="center">
              {submitting && <Spinner size="xs" />}
              {translate('required-actions.dialog.save')}
            </Cluster>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const ActionDialog: FC<ActionDialogProps> = (props) => {
  if (props.action?.type === 'authorizeBooking') {
    return <AuthorizeBookingDialog {...props} />
  }
  return <GenericActionDialog {...props} />
}
